import React, { useEffect, useState } from "react";
import { Descriptions, Spin, Divider, Tag, Empty, message } from "antd";
import moment from "moment";
import {
  getReportDetail,
  REPORT_REASON_LABELS,
  CONTENT_TYPE_LABELS,
} from "../../Services/moderationApi";
import StatusPill from "../../Components/Moderation/StatusPill";
import ReportActionPanel from "../../Components/Moderation/ReportActionPanel";

const partyLine = (p) => {
  if (!p) return "—";
  const name = p.name && p.name.trim() ? p.name : "(no name)";
  const type = p.type ? ` · ${p.type}` : "";
  const strikes =
    p.total_strikes > 0 ? ` · ${p.total_strikes} strike(s)` : "";
  const suspended = p.is_suspended ? " · SUSPENDED" : "";
  return `${name}${type}${strikes}${suspended}`;
};

/**
 * Full detail of one report plus the admin action panel.
 * Rendered inside a Modal by Reports.js.
 */
const ReportDetail = ({ reportId, onActioned }) => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const resp = await getReportDetail(reportId);
      if (resp?.data?.error) {
        message.error(resp.data.error);
        setReport(null);
      } else {
        setReport(resp?.data?.data || null);
      }
    } catch (err) {
      message.error("Could not load the report");
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (reportId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spin />
      </div>
    );
  }
  if (!report) {
    return <Empty description="Report not found" />;
  }

  const snapshot = report.content_snapshot;

  return (
    <div>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Status">
          <StatusPill status={report.status} />
          {report.is_overdue && report.status === "pending" ? (
            <Tag color="red" style={{ marginLeft: 8 }}>
              OVERDUE
            </Tag>
          ) : null}
        </Descriptions.Item>
        <Descriptions.Item label="Reason">
          {REPORT_REASON_LABELS[report.reason] || report.reason}
        </Descriptions.Item>
        <Descriptions.Item label="Content type">
          {CONTENT_TYPE_LABELS[report.content_type] || report.content_type}
        </Descriptions.Item>
        <Descriptions.Item label="Reported by">
          {partyLine(report.reporter)}
        </Descriptions.Item>
        <Descriptions.Item label="Reported user">
          {partyLine(report.accused)}
        </Descriptions.Item>
        <Descriptions.Item label="Details from reporter">
          {report.details || "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Content snapshot">
          {snapshot
            ? typeof snapshot === "string"
              ? snapshot
              : JSON.stringify(snapshot, null, 2)
            : "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Currently hidden">
          {report.flag
            ? report.flag.is_hidden
              ? "Yes"
              : "No (restored)"
            : "No flag"}
        </Descriptions.Item>
        <Descriptions.Item label="Created">
          {report.created_at
            ? moment(report.created_at).format("DD MMM YYYY HH:mm")
            : "—"}
        </Descriptions.Item>
        <Descriptions.Item label="SLA deadline">
          {report.sla_deadline
            ? moment(report.sla_deadline).format("DD MMM YYYY HH:mm")
            : "—"}
        </Descriptions.Item>
        {report.reviewed_at ? (
          <Descriptions.Item label="Reviewed">
            {moment(report.reviewed_at).format("DD MMM YYYY HH:mm")}
            {report.action_taken ? ` · ${report.action_taken}` : ""}
          </Descriptions.Item>
        ) : null}
        {report.admin_note ? (
          <Descriptions.Item label="Admin note">
            {report.admin_note}
          </Descriptions.Item>
        ) : null}
      </Descriptions>

      <Divider />

      <ReportActionPanel
        report={report}
        onDone={() => {
          load();
          onActioned && onActioned();
        }}
      />
    </div>
  );
};

export default ReportDetail;
